// The question asked before grid edits are thrown away.
//
// Pending edits live only in EntityGridPage's state — there is no draft saved
// anywhere — so anything that unmounts that page loses them. Twenty minutes of
// somebody's cleanup work disappearing to a misplaced click is not a thing to
// leave to chance, which is why leaving is a question rather than an action.
//
// ⚠ THE GAP THIS CLOSES: it was already asked for two of the three ways out —
// switching entity tab, and closing the browser (beforeunload) — but NOT for
// in-app links. "Find duplicates", "← Instance" and Billing are ordinary SPA
// navigations that unmount the page silently, and they sit in the same toolbar
// as the edits. Partial coverage is arguably worse than none: it teaches the
// customer that the product asks before losing their work, right up until the
// click where it does not.
//
// The copy lives here so all of them say the same thing, and so it can be
// tested without rendering the grid.

export interface PendingEditsWarning {
  title: string;
  body: string;
}

/**
 * What to say when leaving would discard edits.
 *
 * `destination` is named rather than described generically — "Discard and go
 * to Billing" tells someone what they are about to do in a way that "Discard
 * and leave" does not.
 */
export function pendingEditsWarning(
  changeCount: number,
  destination: string
): PendingEditsWarning {
  const plural = changeCount === 1 ? '' : 's';
  return {
    title: `You have ${changeCount} unsaved change${plural}`,
    // ⚠ "have not been sent to Acumatica" is the load-bearing half. The first
    // question anyone asks is whether the edits already reached their ERP.
    body:
      `Going to ${destination} discards them — they have not been sent to ` +
      `Acumatica. Preview and confirm them first if you want to keep them.`,
  };
}

/** Whether leaving needs to be confirmed at all. */
export function shouldConfirmLeave(changeCount: number): boolean {
  return changeCount > 0;
}
